import {forwardRef, useState} from 'react';
import {twMerge} from '../../../common/twMerge';
import {PC_VIDEO_VIEWER_SEEKBAR_HEIGHT_PX} from '../../../domain/content/[seriesId]/viewer/[productId]/constant/viewerConstant';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';

import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {Slider} from '../../molecules/Slider/Slider';
import {PcVideoViewerSeekbarProps} from './PcVideoViewerSeekbarProps.interface';

export const PcVideoViewerSeekbar = forwardRef<
  HTMLDivElement,
  PcVideoViewerSeekbarProps
>(function PcVideoViewerSeekbar(
  {
    currentProgress,
    loadedProgress,
    currentTime,
    totalTime,
    isMute,
    volume,
    onClickMute,
    onChangeProgress,
    onPressProgressBar,
    onReleaseProgressBar,
    onClickFullscreenBtn,
    onChangeVolume,
    className,
    isShortsDrama,
    ...props
  },
  ref
) {
  const [isVisibleSoundbar, setIsVisibleSoundbar] = useState<boolean>(false);
  const [isPressingSoundIndicator, setIsPressingSoundIndicator] =
    useState<boolean>(false);

  return (
    <div
      className={twMerge(
        'absolute bottom-0 left-0 flex w-full flex-col items-center justify-between bg-transparent px-15pxr',
        className
      )}
      style={{
        height: `${PC_VIDEO_VIEWER_SEEKBAR_HEIGHT_PX}px`,
      }}
      {...props}
    >
      <Slider
        isMobile={false}
        loadedValue={loadedProgress}
        currentValue={currentProgress}
        hasIndicator={isShortsDrama}
        onPressSlider={onPressProgressBar}
        onReleaseSlider={onReleaseProgressBar}
        onValueChange={onChangeProgress}
        className={'mx-15pxr h-12pxr w-full px-6pxr'}
        barStyleClassName={'bg-[rgb(var(--colors-static-mono-white)_/_10%)]'}
        valueStyleClassName={'bg-static-mono-white'}
        loadStyleClassName={'bg-[rgb(var(--colors-static-mono-white)_/_20%)]'}
        indicatorStyleClassName={'bg-static-mono-white'}
      />
      <div className="flex h-46pxr w-full flex-row justify-between px-6pxr">
        <div className="flex select-none flex-row items-center">
          <div
            className="flex items-center"
            onMouseOver={() => setIsVisibleSoundbar(true)}
            onMouseLeave={() => setIsVisibleSoundbar(false)}
          >
            {/* 볼륨 아이콘 */}
            <div
              ref={ref}
              // 시크바와의 클릭 충돌 발생 시 음소거 버튼을 우선시하기 위해 z-index를 추가합니다.
              className="z-2 mr-2pxr"
              {...putTiaraObjectProps({
                click: {
                  layer2: isShortsDrama ? '헤더' : '기타',
                  copy: isMute ? 'on' : 'off',
                },
              })}
            >
              <Icon
                type={
                  isMute
                    ? IconType.ViewerMuteStatic
                    : IconType.ViewerSoundStatic
                }
                onClick={onClickMute}
              />
            </div>
            {(isVisibleSoundbar || isPressingSoundIndicator) &&
              !isShortsDrama && (
                <Slider
                  isMobile={false}
                  currentValue={volume}
                  onValueChange={onChangeVolume}
                  className={'h-12pxr w-56pxr px-5pxr'}
                  onPressSlider={() => setIsPressingSoundIndicator(true)}
                  onReleaseSlider={() => setIsPressingSoundIndicator(false)}
                  barStyleClassName={
                    'bg-[rgb(var(--colors-static-mono-white)_/_10%)]'
                  }
                  valueStyleClassName={'bg-static-mono-white'}
                  loadStyleClassName={
                    'bg-[rgb(var(--colors-static-mono-white)_/_20%)]'
                  }
                  indicatorStyleClassName={
                    'bg-static-mono-white w-10pxr h-10pxr'
                  }
                />
              )}
          </div>
          {/* 영상 시간 표시 */}
          <div className="ml-6pxr flex items-center">
            <Text className="font-small2 text-static-mono-10">
              {currentTime}
            </Text>
            {!isShortsDrama && (
              <>
                <Text className="font-small2 px-4pxr text-static-mono-10">
                  /
                </Text>
                <Text className="font-small2 text-static-mono-10">
                  {totalTime}
                </Text>
              </>
            )}
          </div>
        </div>
        {/* 전체 화면 아이콘 */}
        {!isShortsDrama && (
          <Icon
            type={IconType.ViewerSeekbarExpandStatic}
            onClick={onClickFullscreenBtn}
            className="ml-8pxr w-18pxr cursor-pointer"
            {...putTiaraObjectProps({
              click: {
                layer2: '시크바',
              },
            })}
          />
        )}
      </div>
    </div>
  );
});
